import { useTraderTime } from '../contexts/TraderTimeContext';
import React, { useState } from 'react';
import { useArchivedOrders } from '../hooks/useArchivedOrders';
import { OrderResponse } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pagination } from '../components/ui/pagination';
import { Loader2, RefreshCw, Archive, TrendingDown, Calendar } from 'lucide-react';
import { formatAmount as formatAmountUtil } from '../lib/utils';

export const WithdrawArchivePage: React.FC = () => {
    const { formatDate, label } = useTraderTime();
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const { filteredOrders, currentPage, totalPages, totalItems, pageSize, isLoading, error,
        loadOrders, handlePageChange } = useArchivedOrders('withdraw', dateFrom, dateTo, statusFilter);

    const formatAmount = (amount: number, currency?: string) => {
        return formatAmountUtil(amount, currency);
    };

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            active: { label: 'Активный', variant: 'default' as const },
            completed: { label: 'Завершен', variant: 'secondary' as const },
            cancelled: { label: 'Отменен', variant: 'destructive' as const },
        };

        const config = statusConfig[status as keyof typeof statusConfig] || {
            label: status,
            variant: 'secondary' as const,
        };

        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const renderOrderCard = (order: OrderResponse) => (
        <div
            key={order.id}
            className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-300" />
                        <span className="font-medium">Вывод</span>
                        {getStatusBadge(order.status)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Объём ордера:</p>
                            <p className="font-medium">
                                {formatAmount(order.from_amount)} {order.from_currency}
                            </p>
                            <p className="text-gray-600 dark:text-slate-300">
                                Остаток: {formatAmount(order.remaining_fiat_amount ?? Math.max(0, order.from_amount - Math.max(order.reserved_amount || 0, order.accumulated_amount || 0)))} {order.from_currency}
                            </p>
                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">Создан:</p>
                            <p>{formatDate(order.created_at)}</p>
                        </div>

                        <div>
                            <p className="text-gray-600 dark:text-slate-300">ID:</p>
                            <p className="font-mono text-xs">{order.id}</p>
                        </div>

                        {/* Информация о выводе */}
                        <div className="md:col-span-2 lg:col-span-3">
                            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                                <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
                                    💸 Ордер на вывод
                                </p>
                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                    Реквизиты клиентов, на которые вы переводили средства, можно посмотреть в разделе "История" → конкретная транзакция
                                </p>
                            </div>
                        </div>

                        {/* Итоговая статистика */}
                        {order.accumulated_amount !== undefined && (
                            <div className="md:col-span-2 lg:col-span-3">
                                <p className="text-gray-600 dark:text-slate-300">Обработано:</p>
                                <div className="mt-1">
                                    <span className="font-medium">
                                        {formatAmount(order.accumulated_amount)} из {formatAmount(order.from_amount)} {order.from_currency}
                                    </span>
                                    <span className="text-gray-500 dark:text-slate-400 ml-2">
                                        ({formatAmountUtil((order.accumulated_amount / order.from_amount) * 100)}%)
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Архив реквизитов вывода</h1>
                    <p className="text-gray-600 dark:text-slate-300 mt-2">
                        Завершенные и отмененные реквизиты для вывода
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => loadOrders(currentPage)}
                    disabled={isLoading}
                >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Обновить
                </Button>
            </div>

            {/* Фильтры */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5" />
                        Фильтры · {label}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="dateFrom">Дата создания реквизита от</Label>
                            <Input
                                id="dateFrom"
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="dateTo">Дата создания реквизита до</Label>
                            <Input
                                id="dateTo"
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="statusFilter">Статус</Label>
                            <select
                                id="statusFilter"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md"
                            >
                                <option value="all">Все</option>
                                <option value="completed">Завершен</option>
                                <option value="cancelled">Отменен</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                                setStatusFilter('all');
                            }}
                        >
                            Сбросить фильтры
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {error && (
                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="ml-2">Загрузка архива...</span>
                </div>
            ) : filteredOrders.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Archive className="h-16 w-16 text-gray-400 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">Архив пуст</h3>
                        <p className="text-gray-500 dark:text-slate-400 text-center">
                            Здесь будут отображаться завершенные и отмененные реквизиты вывода
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filteredOrders.map(order => renderOrderCard(order))}

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalItems={totalItems}
                        pageSize={pageSize}
                    />
                </div>
            )}
        </div>
    );
};

